import React from 'react';

import type { ConfigFieldValue } from '@/components/services';
import type { WorkflowConfigFieldSchema } from '@/services/api/generated/firmsService';

import { MultiSelectField } from './MultiSelectField';
import { NumberField } from './NumberField';
import { SingleSelectField } from './SingleSelectField';
import { TextField } from './TextField';
import { TextListField } from './TextListField';

interface ConfigFieldProps {
  field: WorkflowConfigFieldSchema;
  value: ConfigFieldValue;
  onChange: (value: ConfigFieldValue) => void;
  disabled?: boolean;
  isLoading?: boolean;
}

/** Type guard for string value */
const isString = (value: ConfigFieldValue): value is string =>
  typeof value === 'string';

/** Type guard for string array value */
const isStringArray = (value: ConfigFieldValue): value is string[] =>
  Array.isArray(value) && value.every((v) => typeof v === 'string');

/** Type guard for number value */
const isNumber = (value: ConfigFieldValue): value is number =>
  typeof value === 'number';

/**
 * Discriminated field renderer.
 * Routes to the appropriate field component based on field_type from backend.
 */
export const ConfigField: React.FC<ConfigFieldProps> = ({
  field,
  value,
  onChange,
  disabled = false,
  isLoading = false,
}) => {
  const options = field.options ?? [];

  switch (field.field_type) {
    case 'single_select':
      return (
        <SingleSelectField
          label={field.label}
          description={field.description}
          value={isString(value) ? value : null}
          options={options}
          onChange={onChange}
          disabled={disabled}
          isLoading={isLoading}
          status={field.status}
        />
      );

    case 'multi_select':
      return (
        <MultiSelectField
          label={field.label}
          description={field.description}
          values={isStringArray(value) ? value : []}
          options={options}
          onChange={onChange}
          disabled={disabled}
          isLoading={isLoading}
          status={field.status}
        />
      );

    case 'text':
      return (
        <TextField
          label={field.label}
          description={field.description}
          value={isString(value) ? value : null}
          onChange={onChange}
          disabled={disabled}
          isLoading={isLoading}
          status={field.status}
        />
      );

    case 'number':
      return (
        <NumberField
          label={field.label}
          description={field.description}
          value={isNumber(value) ? value : null}
          onChange={onChange}
          disabled={disabled}
          isLoading={isLoading}
          status={field.status}
        />
      );

    case 'text_list':
      return (
        <TextListField
          label={field.label}
          description={field.description}
          values={isStringArray(value) ? value : []}
          onChange={onChange}
          disabled={disabled}
          isLoading={isLoading}
          status={field.status}
        />
      );

    default:
      return null;
  }
};
